import React, { useEffect } from "react";
import "./Services.css";
import AOS from "aos";
import "aos/dist/aos.css";
import PageHeader from "./PageHeader";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay } from "swiper/modules";
import "swiper/css";

// Surgery Department Services
const surgeryServices = [
  { name: "Laparoscopic Surgery", desc: "Advanced minimally invasive laparoscopic procedures.", icon: "/images/services/laproscopy.webp", link: "/services/laparoscopic-surgery" },
  { name: "Gastrointestinal Endoscopy", desc: "Diagnostic and therapeutic endoscopy for GI problems.", icon: "/images/services/endoscopy.webp", link: "/services/gi-endoscopy" },
  { name: "Hernia / Hydrocele / Appendix", desc: "Expert surgical care for hernia, hydrocele, and appendix issues.", icon: "/images/services/hernia.webp", link: "/services/hernia-hydrocele-appendix" },
  { name: "Stomach & Intestine Problems", desc: "Treatment for stomach, intestine, acidity, gall bladder & kidney issues.", icon: "/images/services/stomach.webp", link: "/services/stomach-intestine" },
  { name: "Cancer Treatments", desc: "Breast, intestine and testicular cancer treatment.", icon: "/images/services/cancer.webp", link: "/services/cancer-treatment" },
  { name: "Piles, Fissure, Fistula (Laser Surgery)", desc: "Advanced laser treatment for piles, fissure & fistula.", icon: "/images/services/piles.webp", link: "/services/piles-fissure-fistula" },
];

// Gynaecology Department Services
const gynaecServices = [
  { name: "Pregnancy Care", desc: "Complete antenatal and pregnancy care.", icon: "/images/services/pregnancy.webp", link: "/services/pregnancy-care" },
  { name: "Normal & Cesarean Delivery", desc: "Safe delivery services including normal and C-section.", icon: "/images/services/delivery.webp", link: "/services/delivery" },
  { name: "Family Planning", desc: "Counselling and family planning procedures.", icon: "/images/services/family.webp", link: "/services/family-planning" },
  { name: "Menstrual Cycle Issues", desc: "Treatment for menstrual irregularities and pain.", icon: "/images/services/menstrual.webp", link: "/services/menstrual-issues" },
  { name: "IVF (Test Tube Baby)", desc: "Fertility support and IVF guidance.", icon: "/images/services/ivf.webp", link: "/services/ivf" },
  { name: "Female Urinary Problems", desc: "Treatment for infections and urinary incontinence.", icon: "/images/services/urinary.webp", link: "/services/urinary-problems" },
  { name: "PV Bleeding", desc: "Diagnosis and treatment for abnormal PV bleeding.", icon: "/images/services/pv.webp", link: "/services/pv-bleeding" },
  { name: "Vaginal Problems", desc: "Care for vaginal infections and other issues.", icon: "/images/services/vp.webp", link: "/services/vaginal-problems" },
];

const Services = () => {
  useEffect(() => {
    AOS.init({
      duration: 1000,
      once: false,
      mirror: true,
    });
  }, []);

  // Dynamic SEO: title, meta, JSON-LD
  useEffect(() => {
    document.title = "Best Hospital in Indapur | Laparoscopic Surgery, IVF, Pregnancy Care";

    const metaDescription = document.createElement("meta");
    metaDescription.name = "description";
    metaDescription.content =
      "Dr. Chavan Multispeciality Hospital in Indapur offers top surgical and gynecology services including laparoscopic surgery, cancer treatments, pregnancy care, IVF, piles treatment, and complete women's health care.";
    document.head.appendChild(metaDescription);

    const metaKeywords = document.createElement("meta");
    metaKeywords.name = "keywords";
    metaKeywords.content =
      "hospital in Indapur, laparoscopic surgery Indapur, endoscopy Indapur, pregnancy care Indapur, IVF Indapur, piles treatment Indapur, gynecologist Indapur";
    document.head.appendChild(metaKeywords);

    const metaRobots = document.createElement("meta");
    metaRobots.name = "robots";
    metaRobots.content = "index, follow";
    document.head.appendChild(metaRobots);

    const canonical = document.createElement("link");
    canonical.rel = "canonical";
    canonical.href = "https://drchavanhospital.in/services";
    document.head.appendChild(canonical);

    // JSON-LD structured data
    const script = document.createElement("script");
    script.type = "application/ld+json";
    const schemaData = {
      "@context": "https://schema.org",
      "@type": "MedicalClinic",
      "name": "Dr. Chavan Multispeciality Hospital",
      "url": "https://drchavanhospital.in/services",
      "image": "https://drchavanhospital.in/images/pg_header.webp",
      "description": "Top hospital in Indapur offering laparoscopic surgery, GI endoscopy, pregnancy care, IVF, piles treatment, cancer treatments, and women's health services.",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Indapur",
        "addressLocality": "Indapur",
        "addressRegion": "Maharashtra",
        "postalCode": "413106",
        "addressCountry": "IN"
      },
      "department": [
        ...surgeryServices.map((service) => ({
          "@type": "MedicalSpecialty",
          "name": service.name,
          "url": `https://drchavanhospital.in/${service.link}`,
        })),
        ...gynaecServices.map((service) => ({
          "@type": "MedicalSpecialty",
          "name": service.name,
          "url": `https://drchavanhospital.in/${service.link}`,
        })),
      ]
    };
    script.text = JSON.stringify(schemaData);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(metaDescription);
      document.head.removeChild(metaKeywords);
      document.head.removeChild(metaRobots);
      document.head.removeChild(canonical);
      document.head.removeChild(script);
    };
  }, []);

  // Helper function to render service cards
  const renderServiceCard = (service, index) => (
    <div
      key={index}
      className="service-card"
      data-aos="fade-right"
      data-aos-delay={(index % 3) * 350}
    >
      <img
        src={service.icon}
        alt={`${service.name} | Dr. Chavan Hospital`}
        loading="lazy"
      />
      <div className="service-card-content">
        <h3>{service.name}</h3>
        <p>{service.desc}</p>
        {/* <a href={service.link} className="learn-more">
          Learn More →
        </a> */}
      </div>
    </div>
  );

  return (
    <>
      <PageHeader
        title="Our Services"
        breadcrumbs={["Home", "Services"]}
        background="/images/pg.webp"
      />

      <div className="services-page">
        {/* Hero Section */}
        <section className="services-hero">
          <div className="hero-left" data-aos="fade-right">
            <h1>Our Medical Services</h1>
            <h2>Complete Medical Care, Superior Expertise</h2>
          </div>
          <div className="hero-right" data-aos="fade-left">
            <p>
              Your trusted destination for advanced Surgical and Gynaecology
              services including laparoscopic surgery, cancer treatment,
              gastrointestinal solutions, piles and fistula care. We also provide
              complete women’s health support — pregnancy, cesarean and normal
              delivery, IVF, menstrual problems and vaginal care. Every treatment
              is supported by expert doctors, modern technology and a commitment
              to quicker recovery.
            </p>
          </div>
        </section>

        {/* Surgery Department */}
        <section className="services-overview">
          <h2 className="section-title" data-aos="fade-up">
            Surgery Department
          </h2>

          <div className="services-grid desktop-only">
            {surgeryServices.map(renderServiceCard)}
          </div>

          <div className="mobile-only">
            <Swiper
              modules={[Autoplay]}
              spaceBetween={20}
              slidesPerView={1}
              loop={true}
              autoplay={{ delay: 2500, disableOnInteraction: false }}
            >
              {surgeryServices.map((service, index) => (
                <SwiperSlide key={index}>{renderServiceCard(service, index)}</SwiperSlide>
              ))}
            </Swiper>
          </div>
        </section>

        {/* Gynaecology Department */}
        <section className="services-overview">
          <h2 className="section-title" data-aos="fade-up">
            Gynaecology Department
          </h2>

          <div className="services-grid desktop-only">
            {gynaecServices.map(renderServiceCard)}
          </div>

          <div className="mobile-only">
            <Swiper
              modules={[Autoplay]}
              spaceBetween={20}
              slidesPerView={1}
              loop={true}
              autoplay={{ delay: 2500, disableOnInteraction: false }}
            >
              {gynaecServices.map((service, index) => (
                <SwiperSlide key={index}>{renderServiceCard(service, index)}</SwiperSlide>
              ))}
            </Swiper>
          </div>
        </section>
      </div>
    </>
  );
};

export default Services;
